import {
  Material_Read,
  Material_Read_Nested,
  Material_Read_Typeahead,
  MaterialState,
  MaterialType_Create,
  MaterialType_Read,
  MaterialType_Read_Nested,
  MaterialType_Update,
} from '@treadinc/horizon-api-spec';
import dayjs, { Dayjs } from 'dayjs';
import { t } from 'i18next';

import { CompanyBasic } from '~hooks/useCompany/models';
import { ItemNameAndId } from '~types/ItemNameAndId';
import { Nullable } from '~types/Nullable';

const materialStateOptions = [
  { id: 'active', name: t('common.active') },
  { id: 'inactive', name: t('common.inactive') },
];
export class MaterialTypeItem {
  public static parse(proto: MaterialType_Read_Nested) {
    return new MaterialTypeItem(
      proto.name,
      proto.id,
      proto.system,
      proto.discarded_at ? dayjs(proto.discarded_at) : null,
    );
  }
  public get system() {
    return this._system;
  }
  public get name() {
    return this._name;
  }
  public get id() {
    return this._id;
  }
  public get discardedAt() {
    return this._discarded_at;
  }
  constructor(
    private _name: string,
    private _id: string,
    private _system: boolean,
    private _discarded_at: Dayjs | null = null,
  ) {}
}

export class MaterialType {
  public static parse(proto: MaterialType_Read) {
    return new MaterialType(
      proto.name,
      proto.id,
      proto.system,
      proto.external_id ?? null,
      proto.source,
      proto.discarded_at ? dayjs(proto.discarded_at) : null,
    );
  }
  public static deparse(data: MaterialType): MaterialType_Create {
    return {
      name: data.name,
      external_id: data.externalId,
    };
  }
  public static deparseUpdate(data: MaterialType): MaterialType_Update {
    return {
      name: data.name,
      external_id: data.externalId,
    };
  }
  public get system() {
    return this._system;
  }
  public get name() {
    return this._name;
  }
  public get id() {
    return this._id;
  }
  public get externalId() {
    return this._external_id;
  }
  public get source() {
    return this._source;
  }
  public get discardedAt() {
    return this._discarded_at;
  }
  constructor(
    private _name: string,
    private _id: string,
    private _system: boolean,
    private _external_id: Nullable<string>,
    private _source: string,
    private _discarded_at: Dayjs | null = null,
  ) {}
}
export class BasicMaterial extends ItemNameAndId {
  public static parse(proto: Material_Read_Nested): BasicMaterial {
    return new BasicMaterial(
      proto.id ?? '',
      proto.name ?? '',
      proto.external_id ?? null,
      proto.discarded_at ? dayjs(proto.discarded_at) : null,
    );
  }

  public get externalId() {
    return this._external_id;
  }

  public get discardedAt() {
    return this._discarded_at;
  }

  constructor(
    _id: string,
    _name: string,
    private _external_id: Nullable<string>,
    private _discarded_at: Dayjs | null = null,
  ) {
    super(_name, _id);
  }
}

export class Material {
  public static parse(proto: Material_Read) {
    const stateOption = materialStateOptions.find((option) => option.id === proto.state);

    return new Material(
      proto.id ?? '',
      proto.name ?? '',
      proto.system ?? false,
      MaterialTypeItem.parse(proto.material_type),
      proto.company ? CompanyBasic.parse(proto.company) : null,
      proto.external_id ?? null,
      proto.state ? ItemNameAndId.parse(stateOption) : null,
      dayjs(proto.created_at),
      proto.discarded_at ? dayjs(proto.discarded_at) : null,
    );
  }

  public static deparse(data: Material) {
    return {
      name: data.name,
      external_id: data.externalId,
      state: data.isNotActive ? MaterialState.INACTIVE : MaterialState.ACTIVE,
      company_id: data.company?.id,
      material_type_id: data.type?.id,
    };
  }

  public get system() {
    return this._system;
  }

  public get name() {
    return this._name || '';
  }

  public get id() {
    return this._id;
  }

  public get type() {
    return this._type;
  }

  public get typeName() {
    return this._type.name || '';
  }

  public get company() {
    return this._company;
  }

  public get companyName() {
    return this._company?.legalName || '';
  }

  public get isActive() {
    return this._state?.id === MaterialState.ACTIVE;
  }

  public set isActive(value: boolean) {
    this._state = value
      ? ItemNameAndId.parse(materialStateOptions?.[0])
      : ItemNameAndId.parse(materialStateOptions?.[1]);
  }

  public get isNotActive() {
    return !this.isActive;
  }

  public set isNotActive(value: boolean) {
    this.isActive = !value;
  }

  public get externalId() {
    return this._externalId;
  }

  public get state() {
    return this._state;
  }

  public get createdAt() {
    return this._createdAt;
  }

  public get discardedAt() {
    return this._discarded_at;
  }

  constructor(
    private _id: string,
    private _name: string,
    private _system: boolean,
    private _type: MaterialTypeItem,
    private _company: Nullable<CompanyBasic>,
    private _externalId: Nullable<string>,
    private _state: Nullable<ItemNameAndId>,
    private _createdAt: Dayjs,
    private _discarded_at: Dayjs | null = null,
  ) {}
}

export class MaterialTypeahead {
  constructor(
    private _id: string,
    private _name: string,
    private _external_id: string | null,
  ) {}

  public get id() {
    return this._id;
  }

  public get name() {
    return this._name;
  }

  public get externalId() {
    return this._external_id;
  }

  public static parse(proto: Material_Read_Typeahead) {
    return new MaterialTypeahead(proto.id, proto.name, proto.external_id);
  }
}
